import React, { useRef, useEffect, useState } from 'react';
import './Canvas.css';

function Canvas({ 
  activeTool, 
  currentColor, 
  zoomLevel, 
  showGrid, 
  layers, 
  onColorPick,
  pixelData,
  onPixelDataChange
}) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [canvasSize] = useState({ width: 32, height: 32 }); // 32x32 픽셀 캔버스
  const [lastPixelPos, setLastPixelPos] = useState(null); // 마지막으로 그린 픽셀 위치
  
  // 활성화된 레이어 찾기
  const activeLayer = layers.find(layer => layer.active) || layers[0];
  
  // 캔버스 초기화
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // 배경을 투명하게 설정
    ctx.fillStyle = 'transparent';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // 저장된 픽셀 데이터 그리기
    Object.entries(pixelData).forEach(([key, data]) => {
      const [layerId, x, y] = key.split('-').map(Number);
      const layer = layers.find(l => l.id === layerId);
      
      if (layer && layer.visible) {
        const pixelSize = canvas.width / canvasSize.width;
        ctx.fillStyle = data.color;
        ctx.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize);
      }
    });
    
    // 그리드 그리기
    if (showGrid) {
      const pixelSize = canvas.width / canvasSize.width;
      ctx.strokeStyle = '#555555';
      ctx.lineWidth = 0.5;
      
      for (let x = 0; x <= canvasSize.width; x++) {
        ctx.beginPath();
        ctx.moveTo(x * pixelSize, 0);
        ctx.lineTo(x * pixelSize, canvas.height);
        ctx.stroke();
      }
      
      for (let y = 0; y <= canvasSize.height; y++) {
        ctx.beginPath();
        ctx.moveTo(0, y * pixelSize);
        ctx.lineTo(canvas.width, y * pixelSize);
        ctx.stroke();
      }
    }
  }, [pixelData, showGrid, canvasSize, layers, zoomLevel]);
  
  // 픽셀 위치 계산
  const getPixelCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = Math.floor((e.clientX - rect.left) * scaleX / (canvas.width / canvasSize.width));
    const y = Math.floor((e.clientY - rect.top) * scaleY / (canvas.height / canvasSize.height));
    
    return { x, y };
  };
  
  // 픽셀 그리기
  const drawPixel = (x, y) => {
    if (x < 0 || x >= canvasSize.width || y < 0 || y >= canvasSize.height) return;
    
    // 이미 같은 위치에 같은 도구로 그렸다면 중복 처리 방지
    const posKey = `${x}-${y}`;
    if (lastPixelPos === posKey) return;
    setLastPixelPos(posKey);
    
    if (activeTool === 'brush') {
      const newPixelData = {
        ...pixelData,
        [`${activeLayer.id}-${x}-${y}`]: { color: currentColor }
      };
      onPixelDataChange(newPixelData);
    } else if (activeTool === 'eraser') {
      const newPixelData = { ...pixelData };
      delete newPixelData[`${activeLayer.id}-${x}-${y}`];
      onPixelDataChange(newPixelData);
    } else if (activeTool === 'eyedropper') {
      // 모든 레이어에서 색상 추출 (위에서부터 아래로)
      for (const layer of layers) {
        if (layer.visible) {
          const key = `${layer.id}-${x}-${y}`;
          if (pixelData[key]) {
            // 색상 추출 및 상위 컴포넌트로 전달
            onColorPick(pixelData[key].color);
            return;
          }
        }
      }
    } else if (activeTool === 'fill') {
      // 페인트 통 도구 - 재귀 대신 반복문 사용
      const targetKey = `${activeLayer.id}-${x}-${y}`;
      const targetColor = pixelData[targetKey]?.color || 'transparent';
      
      // 이미 같은 색상이면 아무것도 하지 않음
      if (targetColor === currentColor) return;
      
      // 너비 우선 탐색(BFS) 사용
      const queue = [{x, y}];
      const visited = new Set();
      const newPixelData = { ...pixelData };
      
      while (queue.length > 0) {
        const {x, y} = queue.shift();
        const key = `${activeLayer.id}-${x}-${y}`;
        
        // 이미 방문했거나 캔버스 범위를 벗어나면 건너뜀
        if (visited.has(key) || 
            x < 0 || x >= canvasSize.width || 
            y < 0 || y >= canvasSize.height) {
          continue;
        }
        
        visited.add(key);
        
        // 현재 픽셀 색상 확인
        const currentPixelColor = pixelData[key]?.color || 'transparent';
        
        // 타겟 색상과 같으면 색상 변경 및 인접 픽셀 큐에 추가
        if (currentPixelColor === targetColor) {
          newPixelData[key] = { color: currentColor };
          
          // 인접 픽셀 추가
          queue.push({x: x + 1, y});
          queue.push({x: x - 1, y});
          queue.push({x, y: y + 1});
          queue.push({x, y: y - 1});
        }
      }
      
      onPixelDataChange(newPixelData);
    }
  };
  
  // 마우스 이벤트 핸들러
  const handleMouseDown = (e) => {
    setIsDrawing(true);
    setLastPixelPos(null); // 새로운 그리기 시작 시 마지막 위치 초기화
    const { x, y } = getPixelCoords(e);
    drawPixel(x, y);
  };
  
  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPixelCoords(e);
    drawPixel(x, y);
  };
  
  const handleMouseUp = () => {
    setIsDrawing(false);
    setLastPixelPos(null); // 그리기 종료 시 마지막 위치 초기화
  };

  return (
    <div className="canvas-container">
      <div 
        className="canvas-wrapper"
        style={{ transform: `scale(${zoomLevel})` }}
      >
        <canvas
          ref={canvasRef}
          width={320}
          height={320}
          className="pixel-canvas"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      </div>
    </div>
  );
}

export default Canvas;